import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { fetchEventBySlug } from "@/lib/api/events";
import {
  signupLinkMeetupParticipant,
  completeMeetupParticipantProfile,
} from "@/lib/api/participants";
import { saveParticipantSession } from "@/lib/participant-session";
import { useAuthUser } from "@/lib/use-auth-user";

import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Field } from "@/components/ui/Field";
import { Input } from "@/components/ui/Input";
import { Loader, ErrorBlock } from "@/components/ui/States";
import { MobileFrame } from "@/components/ui/MobileFrame";

export function ContinueRegistrationPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { user, loading: userLoading } = useAuthUser();

  const [linking, setLinking] = useState(true);
  const [linkError, setLinkError] = useState<string | null>(null);
  const [participantId, setParticipantId] = useState<string | null>(null);

  const [form, setForm] = useState({ role: "", company: "", contact: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const { data: event, isLoading: eventLoading } = useQuery({
    queryKey: ["event-slug", slug],
    queryFn: () => fetchEventBySlug(slug ?? ""),
    enabled: Boolean(slug),
  });

  useEffect(() => {
    if (!event || !user) return;
    let cancelled = false;
    (async () => {
      setLinking(true);
      setLinkError(null);
      try {
        const row = await signupLinkMeetupParticipant(event.id);
        if (cancelled) return;
        setParticipantId(row.id);
        setForm((f) => ({
          role: f.role || (row.role ?? ""),
          company: f.company || (row.company ?? ""),
          contact: f.contact || (row.contact_for_matches ?? ""),
        }));
        if (slug) {
          saveParticipantSession(slug, {
            participantId: row.id,
            eventId: row.event_id,
            name: row.name,
            email: row.email,
          });
        }
      } catch (err) {
        if (cancelled) return;
        setLinkError(err instanceof Error ? err.message : "Failed to link account to event.");
      } finally {
        if (!cancelled) setLinking(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [event, user, slug]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!event || !slug) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      await completeMeetupParticipantProfile({
        eventId: event.id,
        contactForMatches: form.contact.trim(),
        role: form.role.trim() || undefined,
        company: form.company.trim() || undefined,
      });
      // V2 spec step 3: route through the Connect Telegram step before
      // goals so the participant gets the bot wired up early. The step
      // itself auto-skips if the bot is already linked (e.g. user
      // arrived via Telegram OAuth on the join screen).
      navigate(`/e/${slug}/connect-telegram`);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : "Failed to save profile. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (eventLoading || userLoading) {
    return (
      <MobileFrame>
        <Loader label="Loading..." />
      </MobileFrame>
    );
  }

  if (!event) {
    return (
      <MobileFrame>
        <Card padding="lg" className="m-6 text-center">
          <p className="text-[14px] text-[#6c757d]">Event not found.</p>
        </Card>
      </MobileFrame>
    );
  }

  if (linking && !participantId) {
    return (
      <MobileFrame>
        <Loader label="Linking your account to this event..." />
      </MobileFrame>
    );
  }

  if (linkError) {
    return (
      <MobileFrame>
        <div className="flex-1 px-4 py-8">
          <ErrorBlock title="Could not link account" message={linkError} />
        </div>
      </MobileFrame>
    );
  }

  return (
    <MobileFrame>
      <form onSubmit={onSubmit} className="flex-1 px-4 py-6 animate-fade-in" autoComplete="off">
        <h1 className="text-[24px] font-bold tracking-tight text-[#1a1a1a]">
          A few more details
        </h1>
        <p className="mt-2 text-[14px] text-[#6c757d]">
          Almost there for <span className="font-semibold text-[#1a1a1a]">{event.title}</span>
        </p>

        {submitError ? (
          <ErrorBlock title="Couldn't save" message={submitError} className="mt-5" />
        ) : null}

        <div className="mt-6 flex flex-col gap-4">
          <Field label="Role">
            <Input
              value={form.role}
              onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}
              placeholder="Founder, Product Manager, Investor..."
            />
          </Field>
          <Field label="Company">
            <Input
              value={form.company}
              onChange={(e) => setForm((f) => ({ ...f, company: e.target.value }))}
              placeholder="Your company"
            />
          </Field>
          <Field
            label="Contact for matches"
            required
            hint="Only shared with people you mutually match with — make sure it's current."
          >
            <Input
              value={form.contact}
              onChange={(e) => setForm((f) => ({ ...f, contact: e.target.value }))}
              placeholder="@alex_ivanov on Telegram, WhatsApp +351..."
              required
            />
          </Field>

          <Button
            type="submit"
            loading={submitting}
            disabled={!form.contact.trim()}
            fullWidth
          >
            {submitting ? "Saving..." : "Continue"}
          </Button>

          {/* V2-4 lurker entry (spec step 4 footer). The participant
              row already exists by this point — signupLinkMeetupParticipant
              ran on mount — so we just skip past the contact form and
              drop the user into the feed. They'll see a "browsing"
              banner with a CTA back to /goals once they decide to
              actually participate. */}
          <button
            type="button"
            onClick={() => navigate(`/e/${slug}/feed`)}
            className="mt-3 text-center text-[13px] font-semibold text-[#2563eb] hover:underline"
          >
            Just seeing who's coming? Browse without a profile →
          </button>
        </div>
      </form>
    </MobileFrame>
  );
}
